/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { EMPTY_STRING } from '@/data/constants/marketing';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { loginFetcher } from '@/data/Content/Login';
import { logoutFetcher, useLogout } from '@/data/Content/Logout';
import { useNotifications } from '@/data/Content/Notifications';
import { SessionErrorContext } from '@/data/context/sessionError';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { useUser } from '@/data/User';
import { personMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/personMutatorKeyMatcher';
import { processError } from '@/data/utils/processError';
import { SyntheticEvent, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { mutate } from 'swr';

export const DATA_KEY_E_SPOT_DATA_FROM_NAME_DYNAMIC = 'E_SPOT_DATA_FROM_NAME_DYNAMIC';

const SESSION_RESET_KEY = 'dp-session-reset-at';
const SESSION_RESET_COOLDOWN = 15_000;

/**
 * The invalid session cookies are only cleared server-side, on a full page load
 * (`getPageProps` -> `getLayout` -> `getPageDataFromId`). Client-side navigation
 * skips that path, and every request -- guest identity login included -- keeps
 * failing with `_ERR_INVALID_COOKIE` while the stale cookies are still attached.
 * The reset is throttled so a session that cannot be repaired does not turn into
 * a redirect loop.
 */
const recentlyReset = () => {
	try {
		return Date.now() - Number(sessionStorage.getItem(SESSION_RESET_KEY) ?? 0) < SESSION_RESET_COOLDOWN;
	} catch (e) {
		return false;
	}
};

const markReset = () => {
	try {
		sessionStorage.setItem(SESSION_RESET_KEY, String(Date.now()));
	} catch (e) {
		// storage unavailable (private mode); the reset still goes through
	}
};

/**
 * On session errors (expired tokens, account signed-in elsewhere, invalid cookies)
 * clean up the session instead of surfacing raw errors: a full page load lets the
 * server drop the stale cookies and hand back a fresh guest session. Registered
 * users land on the login page, guests stay where they are.
 */
export const useSessionErrorHandler = () => {
	const { sessionError, setSessionError } = useContext(SessionErrorContext);
	const { user } = useUser();
	const router = useNextRouter();
	const RouteLocal = useLocalization('Routes');
	const wasLoggedIn = useRef(false);
	const handling = useRef(false);

	useEffect(() => {
		// remember the last-known login state -- once the session breaks, the user
		// fetch fails and `user` no longer reflects how the session was established
		if (user?.isLoggedIn) {
			wasLoggedIn.current = true;
		}
	}, [user?.isLoggedIn]);

	useEffect(() => {
		if (!sessionError || handling.current) {
			return;
		}
		handling.current = true;
		setSessionError(null);
		if (recentlyReset()) {
			return;
		}
		markReset();
		const loginRoute = `/${RouteLocal.Login.route.t()}`;
		window.location.assign(wasLoggedIn.current ? loginRoute : router.asPath);
	}, [sessionError, setSessionError, router, RouteLocal]);
};

export const useSessionError = () => {
	const { sessionError, setSessionError } = useContext(SessionErrorContext);
	const { user } = useUser();
	const { settings } = useSettings();
	const params = useExtraRequestParameters();
	const { handleLogout } = useLogout();
	const { notifyError } = useNotifications();
	const errorsNLS = useLocalization('SessionError');
	type errorNLSKeys = keyof typeof errorsNLS;
	type ArgTypes = string | number;

	const message = useMemo(() => {
		if (!sessionError) {
			return null;
		} else {
			const messageKey = sessionError.messageKey as errorNLSKeys;
			const titleKey = sessionError.titleKey as errorNLSKeys;
			const errorParameters: [ArgTypes, ArgTypes, ArgTypes, ...ArgTypes[]] = ['', '', ''];
			const messageParameters = sessionError.errorParameters;
			if (messageParameters && messageParameters.length > 0) {
				errorParameters.splice(0, messageParameters.length, ...messageParameters);
			}
			const text =
				sessionError.messageKey in errorsNLS
					? errorsNLS[messageKey].t(errorParameters)
					: sessionError.errorMessage ?? '';
			const title = titleKey && titleKey in errorsNLS ? errorsNLS[titleKey].t() : '';

			return { text, title };
		}
	}, [errorsNLS, sessionError]);

	const onSubmit = useCallback(
		async ({
			email = '',
			logonId = '',
			logonPassword,
		}: {
			/**
			 * @deprecated do not use.
			 */
			email?: string;
			logonId?: string;
			logonPassword: string;
		}) => {
			const data = {
				logonId: logonId || email,
				logonPassword,
			};

			try {
				//  await getUser(cache, context);
				await logoutFetcher(true)(settings?.storeId ?? '', { updateCookies: 'true' }, params);
				await loginFetcher(true)(
					settings?.storeId ?? '',
					{
						updateCookies: 'true',
					},
					data,
					params
				);
				setSessionError(null);
				mutate(personMutatorKeyMatcher(EMPTY_STRING));
				mutate(personMutatorKeyMatcher(DATA_KEY_E_SPOT_DATA_FROM_NAME_DYNAMIC), undefined);
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
			}
		},
		[setSessionError, settings?.storeId, notifyError, params]
	);

	const handleCancel = useCallback(
		async (event: SyntheticEvent) => {
			await handleLogout(event);
			setSessionError(null);
		},
		[handleLogout, setSessionError]
	);

	return {
		message,
		handleCancel,
		onSubmit,
		setSessionError,
		logonId: user?.logonId ?? '',
	};
};
